import React, { useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { neutral } from '_lex/_foundations/phxColors';

export interface ImageTab {
  label: string;
  imageSrc: string;
  imageAlt: string;
}

export interface TabbedImageDisplayProps {
  tabs: ImageTab[];
  instructionText: string;
  /** Height of the image area in px. Omit for body image so it sizes to content. */
  imageHeight?: number;
}

const TabbedImageDisplay: React.FC<TabbedImageDisplayProps> = ({
  tabs,
  instructionText,
  imageHeight,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  if (!tabs.length) return null;

  const currentTab = tabs[selectedIndex];

  const handleTabChange = (_event: React.MouseEvent<HTMLElement>, newIndex: number | null) => {
    if (newIndex !== null) setSelectedIndex(newIndex);
  };

  return (
    <Box
      sx={{
        opacity: 0.9,
        border: '1px solid',
        borderColor: neutral[200],
        borderRadius: 2,
        backgroundColor: neutral[100],
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box onClick={(e) => e.stopPropagation()}>
        <ToggleButtonGroup
          value={selectedIndex}
          exclusive
          onChange={handleTabChange}
          size='small'
          aria-label='image view tabs'
        >
          {tabs.map((tab, index) => (
            <ToggleButton
              key={tab.imageAlt}
              value={index}
              aria-label={tab.imageAlt}
            >
              {tab.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Typography
        variant='body2'
        color='text.secondary'
        textAlign='center'
      >
        {instructionText}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          ...(imageHeight !== undefined
            ? { height: imageHeight }
            : { minHeight: 120 }),
        }}
      >
        <Box
          component='img'
          src={currentTab.imageSrc}
          alt={currentTab.imageAlt}
          sx={{
            maxWidth: '100%',
            ...(imageHeight !== undefined
              ? { maxHeight: '100%', objectFit: 'contain' }
              : { height: 'auto', objectFit: 'contain' }),
          }}
        />
        <Typography
          variant='body2'
          sx={{
            mt: 1,
            mb: 1.5,
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
          }}
        >
          {currentTab.imageAlt}
        </Typography>
      </Box>
    </Box>
  );
};

export default TabbedImageDisplay;
